import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { colors, fonts, radii, spacing } from "@edufadder/ui";
import { AUTHORS, HOW_TO_CITE } from "../content/authors.js";
import { LogoMark } from "../components/Logo.js";
import * as api from "../lib/api.js";
import * as s from "../styles.js";

function initials(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .map((p) => p[0])
    .slice(0, 2)
    .join("");
}

export function AboutPage(): JSX.Element {
  const [version, setVersion] = useState("0.3.1");
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    api.getExtensionRelease().then((r) => r.latest_version && setVersion(r.latest_version)).catch(() => undefined);
  }, []);
  const citation = HOW_TO_CITE(version);

  return (
    <div style={{ ...s.wide, maxWidth: 980 }}>
      <div style={{ display: "flex", gap: spacing(5), alignItems: "center", flexWrap: "wrap" }}>
        <LogoMark size={64} />
        <div>
          <h1 style={{ ...s.heading1, fontSize: 34, margin: 0 }}>About EduFadder</h1>
          <p style={{ ...s.mutedText, fontSize: 15, margin: `${spacing(1)} 0 0` }}>Research notes on popular education ideas, right where people meet them.</p>
        </div>
      </div>
      <hr style={{ ...s.goldRule, marginTop: spacing(5) }} />

      <section aria-labelledby="why">
        <h2 id="why" style={s.sectionTitle}>Why we built it</h2>
        <div style={s.prose}>
          <p>
            Popular ideas about learning travel fast online. Some are well supported by research; others — like teaching to a
            student's “learning style” — are widely believed, including by many teachers, even though careful studies haven't
            found the benefits claimed. These ideas shape how teachers plan lessons, how parents help with homework, and how
            students study.
          </p>
          <p>
            Fact-checks and review articles exist, but people rarely go looking for them while they read. EduFadder brings a
            short, sourced research note to the moment someone actually meets an idea — while browsing, searching, or reading —
            and leaves the decision to them. Its aim is <strong>awareness without absolute judgements</strong>: to help people
            notice, look at the evidence, and think for themselves.
          </p>
        </div>
      </section>

      <section aria-labelledby="how">
        <h2 id="how" style={s.sectionTitle}>How it works</h2>
        <div className="ef-grid-3">
          {[
            ["Curated evidence", "Each edufad entry is drafted by a researcher, reviewed by a different person, and only then published — with every source cited and checked. Fixes go through the same review; nothing is edited silently."],
            ["Careful detection", "The extension recognises ideas on the page with transparent, rule-based matching (not a black-box model), and abstains whenever it isn't confident. It distinguishes pages that promote an idea from pages that question it."],
            ["Privacy first", "Pages are screened on the reader's own device; only passages that mention a known idea are looked at more closely, and nothing read is stored or used for training."],
          ].map(([title, text]) => (
            <div key={title} style={{ ...s.card, marginBottom: 0, borderTop: `4px solid ${colors.gold}` }}>
              <h3 style={{ fontFamily: fonts.serif, color: colors.brand, margin: `0 0 ${spacing(2)}` }}>{title}</h3>
              <p style={{ margin: 0, lineHeight: 1.6 }}>{text}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="authors" aria-labelledby="authors-h" style={{ scrollMarginTop: 90 }}>
        <h2 id="authors-h" style={s.sectionTitle}>The creators</h2>
        <hr style={s.goldRule} />
        <div className="ef-grid-2">
          {AUTHORS.map((a) => (
            <div key={a.id} style={{ ...s.card, marginBottom: 0, borderTop: `6px solid ${colors.brand}` }}>
              <div style={{ display: "flex", gap: spacing(4), alignItems: "center", marginBottom: spacing(4) }}>
                <div
                  aria-hidden="true"
                  style={{ width: 64, height: 64, borderRadius: 999, background: colors.brand, color: colors.gold, border: `3px solid ${colors.gold}`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: fonts.serif, fontWeight: 700, fontSize: 22, flexShrink: 0 }}
                >
                  {initials(a.name)}
                </div>
                <div>
                  <h3 style={{ fontFamily: fonts.serif, fontSize: 21, margin: 0 }}>
                    {a.honorific ? `${a.honorific} ` : ""}
                    {a.name}
                  </h3>
                  <div style={{ fontWeight: 600, color: colors.brand }}>{a.role}</div>
                </div>
              </div>
              <p style={{ ...s.mutedText, fontSize: 13.5, marginTop: 0 }}>{a.affiliation}</p>
              {a.bio.map((p) => (
                <p key={p.slice(0, 24)} style={{ lineHeight: 1.6 }}>
                  {p}
                </p>
              ))}
              <div style={{ display: "flex", flexWrap: "wrap", gap: spacing(2), margin: `${spacing(3)} 0` }}>
                {a.focus.map((f) => (
                  <span key={f} style={{ ...s.chip, background: colors.goldTint }}>
                    {f}
                  </span>
                ))}
              </div>
              {a.links.map((l) => (
                <a key={l.href} href={l.href} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 700, marginRight: spacing(4) }}>
                  {l.label} →
                </a>
              ))}
            </div>
          ))}
        </div>
      </section>

      <section id="cite" aria-labelledby="cite-h" style={{ scrollMarginTop: 90 }}>
        <h2 id="cite-h" style={s.sectionTitle}>How to cite EduFadder</h2>
        <hr style={s.goldRule} />
        <div style={{ ...s.card, fontFamily: "ui-monospace, Menlo, Consolas, monospace", fontSize: 14, lineHeight: 1.6, background: colors.goldTint }}>{citation}</div>
        <button
          type="button"
          style={s.buttonSecondary}
          onClick={() => void navigator.clipboard?.writeText(citation).then(() => setCopied(true))}
        >
          {copied ? "Copied ✓" : "Copy citation"}
        </button>
      </section>

      <section aria-labelledby="fsu">
        <h2 id="fsu" style={s.sectionTitle}>Affiliation</h2>
        <hr style={s.goldRule} />
        <p style={s.prose}>
          EduFadder was developed at Florida State University, in the Instructional Systems &amp; Learning Technologies program.
          EduFadder is a research project in progress, not an official software product or service of Florida State University; the university's
          name is used only to describe where the work was done.
        </p>
        <p style={s.prose}>
          Questions or corrections? Use <Link to="/suggest">suggestions</Link>, or the feedback buttons on any note.
        </p>
      </section>

      <div style={{ height: 1, background: colors.border, margin: `${spacing(10)} 0 ${spacing(4)}`, borderRadius: radii.sm }} />
      <p style={s.mutedText}>
        <Link to="/privacy">Privacy</Link> · <Link to="/guide">How to use</Link> · <Link to="/get-extension">Get the extension</Link>
      </p>
    </div>
  );
}
